import { memo, useContext, useDeferredValue, useEffect } from 'react'
import { ColorValue, StyleSheet, View, ViewStyle } from 'react-native'
import { useDerivedValue } from 'react-native-reanimated'

import { Suit, Value } from 'pokersolver'
import { Palette } from '@ppwr/poker'
import { getDSColorWithOpacity } from '@/design-system/Palette'
import { DS } from '@/design-system'
import { CardSVG } from '@/components/GameAssets/Cards/CardSVG'
import { useSoundEffect } from '@/hooks/useSoundEffect'
import { FlipCard, FlipCardProps } from '@/components/GameAssets/Cards/FlipCard'
import { PokerPowerCardSVG } from '@/components/GameAssets/Cards/PokerPowerCardBackSVG'
import { observer } from 'mobx-react-lite'
import { useStore } from '../../../models'
import { LoungeThemeContext } from '../../../screens/GameContainer/GameContext'

function darkenColor(hex: string, amount: number): string {
  const n = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, Math.round((n >> 16) * (1 - amount)))
  const g = Math.max(0, Math.round(((n >> 8) & 0xff) * (1 - amount)))
  const b = Math.max(0, Math.round((n & 0xff) * (1 - amount)))
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`
}

const CardsColors = {
  gray: getDSColorWithOpacity('Global/Black', 0.3),
  'light-purple': Palette['Brand/Maroon'],
  'dark-purple': '#54032A'
} as const

const CardIconColorSet: {
  'four-colors': Record<Suit, ColorValue>
  'two-colors': Record<Suit, ColorValue>
} = {
  'four-colors': {
    c: DS.palette['Brand/Orange'],
    d: DS.palette['Brand/Maroon'],
    s: DS.palette['Brand/Blue'],
    h: DS.palette['Brand/Pink']
  },
  'two-colors': {
    c: DS.palette['Global/Black'],
    d: DS.palette['Utility/Warning'],
    s: DS.palette['Global/Black'],
    h: DS.palette['Utility/Warning']
  }
}

type ShadowStyle = Pick<ViewStyle, 'shadowColor' | 'shadowOffset' | 'shadowRadius' | 'shadowOpacity' | 'elevation'>

// cards: ("2s" | "2h" | "2d" | "2c" | "3s" | "3h" | "3d" | "3c" | "4s" | "4h" | "4d" | "4c" | "5s" | "5h" | "5d" | "5c" | "6s" | "6h" | "6d" | "6c" | "7s" | "7h" | "7d" | "7c" | "8s" | "8h" | "8d" | ... 24 more ... | "Ac")[]

//
type CardComponentProps = {
  value?: Value
  suit?: Suit
  size?: number
  colorSet?: keyof typeof CardIconColorSet | Record<Suit, ColorValue>
  valueColor?: ColorValue
  shadow?: boolean | ShadowStyle
}

function getColorSet(colorSet: CardComponentProps['colorSet']): (typeof CardIconColorSet)['four-colors']
function getColorSet(colorSet: CardComponentProps['colorSet']): (typeof CardIconColorSet)['two-colors']
function getColorSet(colorSet: CardComponentProps['colorSet']) {
  if (colorSet === 'four-colors') {
    return CardIconColorSet['four-colors']
  }
  if (colorSet === 'two-colors') {
    return CardIconColorSet['two-colors']
  }
  return colorSet
}

function getCardSize(width: number) {
  return {
    width,
    height: Math.round(width * 1.47)
  }
}

const CARD_COMPONENT_WIDTH = 38

type PokerCardFrontProps = CardComponentProps & {
  cardContainerStyle?: ViewStyle
  cardStyle?: ViewStyle
}
const PokerCardFront = memo(({ shadow = true, cardContainerStyle, ...props }: PokerCardFrontProps) => {
  const defaultColor = props?.suit ? CardIconColorSet['two-colors']?.[props?.suit] : CardIconColorSet['two-colors'].c
  const colorSet = getColorSet(props.colorSet)
  const iconColor = props.colorSet && props.suit ? colorSet[props.suit] : defaultColor
  const valueColor = props.valueColor ?? '#000'

  const width = props?.size ?? CARD_COMPONENT_WIDTH

  const cardSize = getCardSize(width)

  const shadowStyle = 'object' === typeof shadow ? shadow : shadow === true ? styles.shadow : undefined

  return (
    <View
      style={[
        containerStyle.cardContainer,
        {
          backgroundColor: DS.palette['Global/White'],
          width: cardSize.width,
          height: cardSize.height
        },
        shadowStyle,
        cardContainerStyle
      ]}
    >
      <CardSVG
        width={cardSize.width}
        height={cardSize.height}
        suit={props.suit}
        value={props.value}
        suitColor={iconColor}
        valueColor={valueColor as string}
        style={props?.cardStyle}
        {...props}
      />
    </View>
  )
})

const containerStyle = StyleSheet.create({
  cardContainer: {
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center'
  }
})

type PokerCardBackProps = {
  size?: CardComponentProps['size']
  shadow?: CardComponentProps['shadow']
  brandComponent?: React.JSX.Element
  cardContainerStyle?: ViewStyle
}

export const PokerCardBack = memo(
  ({ size, shadow = true, brandComponent = <PokerPowerCardSVG />, cardContainerStyle }: PokerCardBackProps) => {
    const width = size ?? CARD_COMPONENT_WIDTH
    const cardSize = getCardSize(width)
    const { cardBackColor } = useContext(LoungeThemeContext)
    const backgroundColor = cardBackColor ? darkenColor(cardBackColor, 0.35) : CardsColors['dark-purple']

    const shadowStyle = 'object' === typeof shadow ? shadow : shadow === true ? styles.shadow : undefined

    return (
      <View
        style={[
          containerStyle.cardContainer,
          {
            backgroundColor,
            flexDirection: 'column',
            width: cardSize.width,
            height: cardSize.height
          },
          shadowStyle,
          cardContainerStyle
        ]}
      >
        {brandComponent}
      </View>
    )
  }
)

type PokerCardSlotProps = {
  size?: CardComponentProps['size']
  brandComponent?: React.JSX.Element
  cardContainerStyle?: ViewStyle
  brandComponentStyle?: ViewStyle
}

export const PokerCardSlot = memo(
  ({ size, brandComponent = <PokerPowerCardSVG />, cardContainerStyle, brandComponentStyle }: PokerCardSlotProps) => {
    const width = size ?? CARD_COMPONENT_WIDTH
    const cardSize = getCardSize(width)
    const { cardBackColor } = useContext(LoungeThemeContext)
    const borderColor = cardBackColor ? darkenColor(cardBackColor, 0.35) : CardsColors['dark-purple']

    return (
      <View
        style={[
          containerStyle.cardContainer,
          {
            borderColor,
            borderStyle: 'dashed',
            borderWidth: 1,
            flexDirection: 'column',
            width: cardSize.width,
            height: cardSize.height
          },
          cardContainerStyle
        ]}
      >
        <View style={[{ width: cardSize.width, height: cardSize.height, opacity: 0.75 }, brandComponentStyle]}>
          {brandComponent}
        </View>
      </View>
    )
  }
)

type FlippableCardProps = CardComponentProps & {
  containerStyle?: ViewStyle
  side?: 'front' | 'back'
  flipOptions?: Pick<FlipCardProps, 'duration' | 'direction'>
  soundEnable?: boolean
  backSideOptions?: Pick<PokerCardBackProps, 'cardContainerStyle' | 'brandComponent'>
  frontSideOptions?: Pick<PokerCardFrontProps, 'cardContainerStyle' | 'cardStyle'>
}

const PokerCardFlippable = ({
  side = 'front',
  containerStyle,
  shadow = true,
  soundEnable = false,
  backSideOptions,
  frontSideOptions,
  ...props
}: FlippableCardProps) => {
  const width = props?.size ?? CARD_COMPONENT_WIDTH
  const cardSize = getCardSize(width)

  const previousSide = useDeferredValue(side)
  const isFlipped = useDerivedValue(() => side !== 'front', [side])

  const { playSound } = useSoundEffect('cardsDealt')

  useEffect(() => {
    if (soundEnable && side !== previousSide) {
      playSound()
    }
  }, [side, previousSide, soundEnable, playSound])

  return (
    <FlipCard
      isFlipped={isFlipped}
      cardStyle={{
        width: cardSize.width,
        height: cardSize.height,
        backfaceVisibility: 'hidden'
      }}
      containerStyle={containerStyle}
      RegularContent={<PokerCardFront size={props.size} {...props} {...frontSideOptions} />}
      FlippedContent={<PokerCardBack size={props.size} shadow={shadow} {...backSideOptions} />}
      {...props.flipOptions}
    />
  )
}

// TODO: remove dependency for MST store, create dedicated store for game config
const ThemedFlippableCard = observer((props: FlippableCardProps) => {
  const {
    game: { isFourColoredDeckEnabled }
  } = useStore()
  return <PokerCardFlippable colorSet={isFourColoredDeckEnabled ? 'four-colors' : 'two-colors'} {...props} />
})

const styles = StyleSheet.create({
  shadow: {
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5
  }
})

export const PokerCard = {
  Front: PokerCardFront,
  Back: PokerCardBack,
  Flip: ThemedFlippableCard,
  Slot: PokerCardSlot
}
